import { useState } from "react";
import { useWebsiteFeedbackSubmissions } from "@/queries/hooks";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatDateTime } from "@/lib/date-utils";
import { Bug } from "lucide-react";
import { FeedbackType } from "@satyrsmc/shared/lib/enums";
import type { WebsiteAdminListFeedbackSubmissionsOutput } from "@satyrsmc/shared/dto/admin/websiteAdmin";

type FeedbackSubmission = WebsiteAdminListFeedbackSubmissionsOutput[number];

const TYPE_LABELS: Record<FeedbackType, string> = {
  [FeedbackType.Bug]: "Bug reports",
  [FeedbackType.FeatureRequest]: "Feature requests",
  [FeedbackType.Comment]: "Comments",
};

function submissionSummary(s: {
  type: FeedbackType;
  bug_steps: string | null;
  feature_description: string | null;
  comment_message: string | null;
}): string {
  if (s.type === FeedbackType.Bug) return s.bug_steps ?? "";
  if (s.type === FeedbackType.FeatureRequest) return s.feature_description ?? "";
  return s.comment_message ?? "";
}

function DetailField({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <p className="text-sm whitespace-pre-wrap">{value}</p>
    </div>
  );
}

export function WebsiteFeedbackSubmissionsPanel() {
  const { data: submissions = [], isLoading } = useWebsiteFeedbackSubmissions();
  const [viewing, setViewing] = useState<FeedbackSubmission | null>(null);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bug className="size-5" />
            Feedback submissions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">
            View submissions from the &quot;Submit Feedback&quot; button — bug reports, feature
            requests, and comments.
          </p>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : submissions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No submissions yet.</p>
          ) : (
            (Object.values(FeedbackType) as FeedbackType[]).map((type) => {
              const group = submissions.filter((s) => s.type === type);
              return (
                <div key={type} className="mb-6 last:mb-0">
                  <h3 className="font-medium mb-2">
                    {TYPE_LABELS[type]} ({group.length})
                  </h3>
                  {group.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No submissions yet.</p>
                  ) : (
                    <ul className="space-y-2">
                      {group.slice(0, 10).map((s) => (
                        <li key={s.id}>
                          <button
                            type="button"
                            onClick={() => setViewing(s)}
                            className="w-full rounded-md border px-3 py-2 text-sm text-left hover:bg-accent"
                          >
                            <span className="font-medium">{s.name}</span>
                            <span className="text-muted-foreground"> — {s.email}</span>
                            <p className="mt-1 text-muted-foreground truncate">
                              {submissionSummary(s)}
                            </p>
                          </button>
                        </li>
                      ))}
                      {group.length > 10 && (
                        <li className="text-sm text-muted-foreground">
                          …and {group.length - 10} more
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      <Dialog open={viewing !== null} onOpenChange={(open) => !open && setViewing(null)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto max-w-2xl">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle>{viewing.name}</DialogTitle>
                <DialogDescription>
                  {viewing.email} · {TYPE_LABELS[viewing.type]} · {viewing.status} ·{" "}
                  {formatDateTime(viewing.created_at)}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {viewing.type === FeedbackType.Bug && (
                  <>
                    <DetailField label="Steps to reproduce" value={viewing.bug_steps} />
                    <DetailField label="URL" value={viewing.bug_url} />
                    <DetailField label="Expected behavior" value={viewing.bug_expected} />
                    <DetailField label="Device type" value={viewing.bug_device_type} />
                    <DetailField label="Browser" value={viewing.bug_browser} />
                    <DetailField label="OS" value={viewing.bug_os} />
                    <DetailField label="Other info" value={viewing.bug_other_info} />
                  </>
                )}
                {viewing.type === FeedbackType.FeatureRequest && (
                  <>
                    <DetailField label="Description" value={viewing.feature_description} />
                    <DetailField label="Page" value={viewing.feature_page} />
                  </>
                )}
                {viewing.type === FeedbackType.Comment && (
                  <DetailField label="Comment" value={viewing.comment_message} />
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
